import {
  FilterGroupDto,
  PaginatedUsersDto,
  PhoneNumberDto,
  UserDto,
  UserForGeoMapDto,
} from '@act/shared/data-transfer-objects';
import {
  ActionReducerMapBuilder,
  PayloadAction,
  current,
} from '@reduxjs/toolkit';
import { resetRedux } from '../actions/sharedActions';
import { MemberState } from '../types/membersTypes';
import parseInteractableResponses from 'redux/utils/interactableResponseParser';
import { summarizeRawEthnicities } from 'redux/utils/raceEthnicityParser.util';
import { getCalls } from '../thunks/calls.thunk';
import { getUsersByIds } from '../thunks/users.thunk';
import { verifyUser } from './actingUserSlice.slice';
import {
  hospitalizationsActions,
  getCurrentAndPendingHospitalizations,
} from './hospitalizations.slice';
import {
  addMembersOnly,
  initialMemberState,
  memberEntityAdapter,
  memberForMapEntityAdapter,
  memberStatusEntityAdapter,
  paginatedMemberEntityAdapter,
} from './memberSliceState.util';
import {
  createAddressSuccess,
  createEmailAddressSuccess,
  createPhoneNumbeUserSuccess,
  createPhoneNumber,
  deletePhoneNumber,
  deletedAddressSuccess,
  deletedEmailAddressSuccess,
  getMember,
  getMemberFilters,
  getMemberInfo,
  getMemberStatuses,
  getMembersForMap,
  getPaginatedMembersForTable,
  newMembersLoaded,
  patchMemberInfo,
  putRaceEthnicityInfo,
  updatePhoneNumber,
  updatedAddressSuccess,
  updatedEmailAddressSuccess,
  updatedPhoneNumberUserSuccess,
  updatedPrimaryAddressSuccess,
} from './memberSliceActions.thunk';
import { getSMSMessages } from './smsMessagesSlice.slice';

export const buildMemberExtraReducers = (
  builder: ActionReducerMapBuilder<MemberState>
) => {
  builder

    .addCase(getMember.fulfilled, (state, action: PayloadAction<UserDto>) => {
      state.status = 'success';
      memberEntityAdapter.upsertOne(state.members, action.payload);
    })

    .addCase(getMembersForMap.pending, state => {
      state.status = 'loading';
      memberForMapEntityAdapter.removeAll(state.membersForMap);
    })
    .addCase(
      getMembersForMap.fulfilled,
      (state, action: PayloadAction<UserForGeoMapDto[]>) => {
        state.status = 'success';
        memberForMapEntityAdapter.addMany(state.membersForMap, action.payload);
      }
    )
    .addCase(getMembersForMap.rejected, state => {
      state.status = 'failed';
    })

    .addCase(getPaginatedMembersForTable.pending, state => {
      state.status = 'loading';
      paginatedMemberEntityAdapter.removeAll(state.paginatedMembers);
    })
    .addCase(
      getPaginatedMembersForTable.fulfilled,
      (state, action: PayloadAction<PaginatedUsersDto>) => {
        state.status = 'success';
        state.count = action.payload.count;
        paginatedMemberEntityAdapter.addMany(
          state.paginatedMembers,
          action.payload.users
        );
      }
    )
    .addCase(getPaginatedMembersForTable.rejected, state => {
      state.status = 'failed';
    })

    .addCase(getMemberFilters.pending, state => {
      state.status = 'loading';
      state.filters = [];
    })
    .addCase(
      getMemberFilters.fulfilled,
      (state, action: PayloadAction<FilterGroupDto>) => {
        state.status = 'success';
        state.filters = action.payload.filters;
      }
    )
    .addCase(getMemberFilters.rejected, state => {
      state.status = 'failed';
    })

    .addCase(getMemberStatuses.pending, state => {
      state.status = 'loading';
    })
    .addCase(getMemberStatuses.fulfilled, (state, action) => {
      state.status = 'success';
      memberStatusEntityAdapter.addMany(state.memberStatuses, action.payload);
    })
    .addCase(getUsersByIds.fulfilled, (state, action) => {
      addMembersOnly(state, action.payload.users);
    })
    .addCase(getCalls.fulfilled, (state, action) => {
      addMembersOnly(state, action.payload.users);
    })
    .addCase(getSMSMessages.fulfilled, (state, action) => {
      addMembersOnly(state, action.payload.users);
    })
    .addCase(getCurrentAndPendingHospitalizations.fulfilled, (state, action) => {
      const members = action.payload.allHospitalizations
        .map(h => h.member)
        .filter((m): m is UserDto => m !== null && m !== undefined);
      addMembersOnly(state, members);
    })

    .addCase(newMembersLoaded, (state, action) => {
      memberEntityAdapter.upsertMany(state.members, action.payload.members);
    })

    .addCase(resetRedux, state => {
      state = { ...initialMemberState };
      return state;
    })
    .addCase(getMemberInfo.fulfilled, (state, action) => {
      memberEntityAdapter.upsertOne(state.members, action.payload);
    })
    .addCase(patchMemberInfo.fulfilled, (state, action) => {
      memberEntityAdapter.upsertOne(state.members, action.payload);
    })
    .addCase(putRaceEthnicityInfo.fulfilled, (state, action) => {
      const member = state.members.entities[action.meta.arg.params.memberId];
      if (member) {
        member.raceEthnicitySummary = summarizeRawEthnicities(action.payload);
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(createPhoneNumber.fulfilled, (state, action) => {
      const member = state.members.entities[action.meta.arg.params.userId];
      if (member) {
        member.phoneNumbers = [...member.phoneNumbers, action.payload];
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(createPhoneNumbeUserSuccess, (state, action) => {
      const member = state.members.entities[action.payload.userId];
      if (member) {
        member.phoneNumbers = [
          ...member.phoneNumbers,
          action.payload.phoneNumber,
        ];
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(updatedPhoneNumberUserSuccess, (state, action) => {
      const member = state.members.entities[action.payload.userId];
      if (member) {
        member.phoneNumbers = member.phoneNumbers.map(phoneNumber => {
          if (
            action.payload.phoneNumber.primary &&
            phoneNumber.id !== action.payload.phoneNumberId
          ) {
            return { ...phoneNumber, primary: false };
          }
          if (phoneNumber.id === action.payload.phoneNumberId) {
            return action.payload.phoneNumber;
          }
          return phoneNumber;
        });
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(deletePhoneNumber.fulfilled, (state, action) => {
      const member = state.members.entities[action.meta.arg.params.userId];
      if (member) {
        member.phoneNumbers = [...member.phoneNumbers].filter(
          pn => current(pn).id !== action.payload.id
        );
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(updatePhoneNumber.fulfilled, (state, action) => {
      const member = state.members.entities[action.meta.arg.params.userId];
      if (member) {
        member.phoneNumbers = parseInteractableResponses(action.payload, [
          ...member.phoneNumbers,
        ] as PhoneNumberDto[]);
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(createAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.userId];
      if (member) {
        member.addresses = [...member.addresses, action.payload.address];
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(updatedAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.userId];
      if (member && member.addresses) {
        const addressIndex = member.addresses.findIndex(
          address => address.id === action.payload.addressId
        );
        if (addressIndex !== -1) {
          member.addresses[addressIndex].deletedAt = Date.now();
        }
        member.addresses = [...member.addresses, action.payload.address];
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(deletedAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.userId];
      if (member && member.addresses) {
        const addressIndex = member.addresses.findIndex(
          address => address.id === action.payload.addressId
        );
        if (addressIndex !== -1) {
          member.addresses[addressIndex].deletedAt = Date.now();
        }
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(verifyUser.fulfilled, (state, action) => {
      memberStatusEntityAdapter.addMany(
        state.memberStatuses,
        action.payload.memberStatuses
      );
    })
    .addCase(hospitalizationsActions.gotHospitalizations, (state, action) => {
      const members = action.payload.data.map(h => h.member);
      memberEntityAdapter.addMany(state.members, members);
    })
    .addCase(updatedPrimaryAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.userId];
      if (member) {
        member.addresses = member.addresses.map(address => {
          if (address.id === action.payload.address.id) {
            return action.payload.address;
          }
          return { ...address, primary: false };
        });
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(createEmailAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.memberId];
      if (member) {
        member.emailAddresses = [
          ...member.emailAddresses,
          action.payload.emailAddress,
        ];
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(updatedEmailAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.memberId];
      if (member) {
        member.emailAddresses = member.emailAddresses.map(emailAddress => {
          if (emailAddress.id === action.payload.emailAddressId) {
            return action.payload.emailAddress;
          }
          if (action.payload.emailAddress.primary) {
            return { ...emailAddress, primary: false };
          }
          return emailAddress;
        });
        memberEntityAdapter.upsertOne(state.members, member);
      }
    })
    .addCase(deletedEmailAddressSuccess, (state, action) => {
      const member = state.members.entities[action.payload.memberId];
      if (member) {
        member.emailAddresses = member.emailAddresses.filter(
          emailAddress => emailAddress.id !== action.payload.emailAddressId
        );
        memberEntityAdapter.upsertOne(state.members, member);
      }
    });
};
